/**
 * Utility class that provides a customizable requestAnimationFrame loop.
 * Allows running a callback function on every frame or at a specific FPS rate.
 * Automatically pauses on `document.hidden` and resumes on visibility change.
 */
export class StringFPS {
  /** Target frames per second (FPS). */
  private fps: number = 0;

  /** Whether the animation loop is currently active. */
  private isAnimationStarted: boolean = false;

  /** Time interval between frames in milliseconds, based on FPS. */
  private fpsInterval: number = 0;

  /** Timestamp of the previous frame (used for timing calculations). */
  private then: number = 0;

  /** The requestAnimationFrame ID (used to cancel the loop). */
  private requestAnimationId: number = 0;

  /** Bound function for visibilitychange event handler. */
  private onVisibilityChangeBind: any;

  /** Callback executed on each frame. */
  private onFrameCallback: (time: number) => void = (time: number) => {};

  /** Internal animation loop function. */
  private animate: () => void = () => {};

  constructor() {
    this.onVisibilityChangeBind = this.onVisibilityChange.bind(this);
  }

  /**
   * Handles visibility change events.
   * Stops the loop when the document is hidden and resumes it when visible.
   */
  private onVisibilityChange() {
    if (document.hidden) {
      this.stop();
      this.isAnimationStarted = false;
    } else {
      this.start(this.fps);
    }
  }

  /**
   * Starts the animation loop at a given FPS rate.
   * If `fps` is 0, runs the callback as fast as possible (uncapped).
   * 
   * @param fps Target frames per second (0 = uncapped).
   */
  public start(fps: number) {
    this.fps = fps;
    if (this.isAnimationStarted) return;

    this.fpsInterval = 1000 / fps;
    this.then = performance.now();
    this.isAnimationStarted = true;

    if (fps === 0) {
      this.animate = () => {
        const now = performance.now();
        this.requestAnimationId = requestAnimationFrame(() => this.animate());
        this.onFrameCallback(now);
      };
    } else {
      this.animate = () => {
        const now = performance.now();
        const elapsed = now - this.then;
        if (elapsed > this.fpsInterval) {
          this.then = now - (elapsed % this.fpsInterval);
          this.onFrameCallback(now);
        }
        this.requestAnimationId = requestAnimationFrame(() => this.animate());
      };
    }

    this.animate();
    // document.addEventListener("visibilitychange", this.onVisibilityChangeBind);
  }

  /**
   * Stops the animation loop and removes the animation frame.
   */
  public stop() {
    if (!this.isAnimationStarted) return;
    cancelAnimationFrame(this.requestAnimationId);
    this.requestAnimationId = 0;
    this.isAnimationStarted = false;
  }

  /**
   * Assigns a callback function to be called on each frame.
   * 
   * @param callback The function to execute per frame.
   */
  public setOnFrame(callback: (time: number) => void) {
    this.onFrameCallback = callback;
  }

  /**
   * Stops the loop and removes event listeners.
   * Should be called when the loop is no longer needed.
   */
  public destructor() {
    this.stop();
    // document.removeEventListener("visibilitychange", this.onVisibilityChangeBind);
  }
}
